<template>
  <section class="invoice-management-page">
    <header
      class="invoice-page-header d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4"
    >
      <div>
        <p class="eyebrow mb-2">
          Finance operations
        </p>
        <h1 class="mb-1">
          Invoice Management
        </h1>
        <p class="text-muted mb-0">
          Select eligible experts by project and prepare invoice exports.
        </p>
      </div>
      <!-- <div class="eligible-summary">
        <strong>{{ totalEligibleExperts }}</strong>
        <span>experts eligible across {{ eligibleProjectCount }} projects</span>
      </div> -->
    </header>

    <invoice-filter-bar
      :companies-url="'/admin/invoice/companies'"
      :result-count="totalProjects"
      @filter-change="onFilterChange"
    />

    <div
      v-if="loading"
      class="text-center py-5"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <template v-else-if="projects.length > 0">
      <invoice-project-card
        v-for="project in projects"
        :key="project.id"
        :project="project"
        :selected-experts="selectedExperts[project.id] || []"
        :date-from="dateFrom"
        :date-to="dateTo"
        @selection-change="onSelectionChange(project.id, $event)"
        @selection-amount-change="onSelectionAmountChange(project.id, $event)"
        @export="onExport(project.id, $event)"
      />
    </template>

    <div
      v-else
      class="empty-invoices card border-0 text-center shadow-sm"
    >
      <div class="card-body py-5">
        <span class="empty-icon mb-3">
          <i class="fa fa-file-invoice-dollar" />
        </span>
        <h3 class="h5 mb-2">
          No invoice-ready projects found
        </h3>
        <p class="text-muted mb-0">
          Try adjusting your filters or check again when payment requests are completed.
        </p>
      </div>
    </div>

    <p
      v-if="!loading && showAll && totalProjects > 0"
      class="text-muted small text-center mb-0 mt-3"
    >
      Showing all {{ totalProjects }} results
    </p>

    <invoice-pagination
      v-if="!loading && totalProjects > 0 && !showAll"
      :current-page="currentPage"
      :total="totalProjects"
      :per-page="perPage"
      @page-change="onPageChange"
      @per-page-change="onPerPageChange"
    />

    <transition name="slide-up">
      <div
        v-if="hasAnySelection"
        class="selection-tray"
      >
        <div class="tray-inner d-flex align-items-center justify-content-between flex-wrap gap-3">
          <span class="tray-summary">
            <i class="fa fa-check-circle me-2 text-primary" />
            <strong>{{ totalSelectedCount }}</strong> payment request(s) selected across
            <strong>{{ selectedProjectCount }}</strong> project(s) ·
            <strong>${{ formatAmount(totalSelectedAmount) }}</strong> total
          </span>
          <div class="d-flex gap-2">
            <button
              v-if="canExportAll"
              class="btn btn-sm btn-primary"
              @click="onExportAll"
            >
              <i class="fa fa-file-export me-1" /> Export All
            </button>
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="clearAllSelections"
            >
              <i class="fa fa-times me-1" /> Clear all
            </button>
          </div>
        </div>
      </div>
    </transition>

    <invoice-export-modal
      v-if="activeProjectId"
      ref="exportModal"
      :key="'export-modal-' + activeProjectId"
      :project-id="activeProjectId"
      :preselected-payment-request-ids="activePaymentRequestIds"
      @export-success="onExportSuccess"
    />

    <invoice-multi-export-modal
      ref="multiExportModal"
      :export-items="multiExportItems"
      @export-success="onMultiExportSuccess"
      @export-cancelled="onMultiExportCancelled"
    />
  </section>
</template>

<script>
import InvoiceExportModal from "../project/InvoiceExportModal.vue";
import InvoiceFilterBar from "./InvoiceFilterBar.vue";
import InvoiceMultiExportModal from "./InvoiceMultiExportModal.vue";
import InvoicePagination from "./InvoicePagination.vue";
import InvoiceProjectCard from "./InvoiceProjectCard.vue";

export default {
    name: "InvoiceManagementPage",
    components: {
        InvoiceExportModal,
        InvoiceFilterBar,
        InvoiceMultiExportModal,
        InvoicePagination,
        InvoiceProjectCard,
    },
    data() {
        return {
            projects: [],
            loading: false,
            search: "",
            clientFilter: "",
            showAll: false,
            perPage: 10,
            currentPage: 1,
            totalProjects: 0,
            totalEligibleExperts: 0,
            eligibleProjectCount: 0,
            selectedExperts: {},
            selectedAmounts: {},
            activeProjectId: null,
            activePaymentRequestIds: [],
            clientNameCache: {},
            datePreset: "all",
            dateFrom: "",
            dateTo: "",
            requestId: 0,
        };
    },
    computed: {
        multiExportItems() {
            const items = [];
            const seen = new Set();

            this.projects.forEach((project) => {
                const ids = this.selectedExperts[project.id];
                if (ids && ids.length > 0) {
                    items.push({
                        projectId: project.id,
                        clientName: project.client_name || "",
                        paymentRequestIds: [...ids],
                    });
                    seen.add(project.id);
                }
            });

            Object.keys(this.selectedExperts).forEach((key) => {
                const n = Number(key);
                const projectId = Number.isFinite(n) ? n : key;
                if (seen.has(projectId)) {
                    return;
                }
                const ids = this.selectedExperts[key];
                if (ids && ids.length > 0) {
                    const project = this.projects.find((p) => p.id === projectId);
                    items.push({
                        projectId,
                        clientName: project?.client_name || this.clientNameCache[projectId] || "",
                        paymentRequestIds: [...ids],
                    });
                }
            });

            return items;
        },
        totalSelectedCount() {
            return Object.values(this.selectedExperts).reduce((s, ids) => s + ids.length, 0);
        },
        totalSelectedAmount() {
            return Object.values(this.selectedAmounts).reduce((s, v) => s + v, 0);
        },
        hasAnySelection() {
            return this.totalSelectedCount > 0;
        },
        selectedProjectCount() {
            return Object.entries(this.selectedExperts).filter(([, ids]) => ids.length > 0).length;
        },
        canExportAll() {
            return this.selectedProjectCount >= 2;
        },
    },
    mounted() {
        this.loadProjects();
    },
    methods: {
        loadProjects() {
            this.loading = true;
            const currentRequestId = ++this.requestId;
            const params = {
                search: this.search,
                per_page: this.perPage,
                page: this.currentPage,
                client_id: this.clientFilter || '',
                date_from: this.dateFrom,
                date_to: this.dateTo,
                ...(this.showAll ? { show_all: 1 } : {}),
            };
            axios.get('/admin/invoice/projects', { params })
                .then(({ data }) => {
                    if (currentRequestId !== this.requestId) return;
                    this.projects = data.data;
                    this.pruneSelections(data.data);
                    data.data.forEach((p) => {
                        this.$set(this.clientNameCache, p.id, p.client_name || "");
                    });
                    this.totalProjects = data.meta.total;
                    this.totalEligibleExperts = data.data.reduce((sum, p) => sum + (p.eligible_expert_count || 0), 0);
                    this.eligibleProjectCount = data.data.filter((p) => (p.eligible_expert_count || 0) > 0).length;
                })
                .finally(() => {
                    if (currentRequestId === this.requestId) {
                        this.loading = false;
                    }
                });
        },
        onFilterChange(filters) {
            this.search = filters.search;
            this.clientFilter = filters.clientId;
            this.showAll = !!filters.showAll;
            this.datePreset = filters.datePreset || "all";
            this.dateFrom = filters.dateFrom || "";
            this.dateTo = filters.dateTo || "";
            this.currentPage = 1;
            this.loadProjects();
        },
        onSelectionChange(projectId, paymentRequestIds) {
            this.$set(this.selectedExperts, projectId, paymentRequestIds);
        },
        onSelectionAmountChange(projectId, amount) {
            this.$set(this.selectedAmounts, projectId, amount);
        },
        clearAllSelections() {
            this.selectedExperts = {};
            this.selectedAmounts = {};
        },
        pruneSelections(visibleProjects) {
            const visibleIds = new Set(visibleProjects.map((p) => p.id));
            Object.keys(this.selectedExperts).forEach((key) => {
                if (!visibleIds.has(Number(key))) {
                    this.$delete(this.selectedExperts, key);
                    this.$delete(this.selectedAmounts, key);
                }
            });
        },
        onExportAll() {
            if (!this.canExportAll) {
                return;
            }
            this.$nextTick(() => this.$refs.multiExportModal?.show());
        },
        onMultiExportSuccess() {
            this.clearAllSelections();
            this.loadProjects();
        },
        onExportSuccess() {
            this.loadProjects();
        },
        onMultiExportCancelled() {
        },
        formatAmount(value) {
            return Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        onExport(projectId, paymentRequestIds) {
            this.activeProjectId = projectId;
            this.activePaymentRequestIds = paymentRequestIds;
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.$refs.exportModal?.show();
                });
            });
        },
        onPageChange(page) {
            this.currentPage = page;
            this.loadProjects();
        },
        onPerPageChange(perPage) {
            this.perPage = perPage;
            this.currentPage = 1;
            this.loadProjects();
        },
    },
};
</script>

<style scoped>
.invoice-management-page {
    background:
        radial-gradient(circle at 96% 3%, rgba(76, 120, 221, .09), transparent 26rem),
        #f6f8f8;
    border-radius: 18px;
    min-height: calc(100vh - 150px);
    padding: 1.75rem;
}

.invoice-page-header h1 {
    color: #1F2937;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -.035em;
}

.eyebrow {
    color: #4c78dd;
    font-size: .7rem;
    font-weight: 700;
    letter-spacing: .16em;
    text-transform: uppercase;
}

.eligible-summary {
    align-items: center;
    background: #1F2937;
    border-radius: 14px;
    color: #fff;
    display: flex;
    gap: .8rem;
    padding: .8rem 1.1rem;
}

.eligible-summary strong {
    color: #9db8f0;
    font-size: 1.7rem;
    line-height: 1;
}

.eligible-summary span {
    font-size: .86rem;
    max-width: 170px;
}

.empty-invoices {
    border-radius: 14px;
}

.empty-icon {
    align-items: center;
    background: #eef2fb;
    border-radius: 50%;
    color: #4c78dd;
    display: inline-flex;
    font-size: 1.4rem;
    height: 62px;
    justify-content: center;
    width: 62px;
}

@media (max-width: 767.98px) {
    .invoice-management-page {
        padding: 1rem;
    }

    .invoice-page-header h1 {
        font-size: 1.65rem;
    }
}

.selection-tray {
    background: #fff;
    border-top: 2px solid #4c78dd;
    border-radius: 0 0 18px 18px;
    bottom: 0;
    box-shadow: 0 -4px 24px rgba(76, 120, 221, .12);
    left: 0;
    padding: .9rem 1.75rem;
    position: sticky;
    right: 0;
    z-index: 10;
}

.tray-summary {
    color: #233e49;
    font-size: .9rem;
}

.slide-up-enter-active,
.slide-up-leave-active {
    opacity: 1;
    transform: translateY(0);
    transition: transform .25s ease, opacity .25s;
}

.slide-up-enter,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(100%);
}
</style>
